/**
 * FindingCard Component
 * Expandable finding card with E3/E4 integration:
 * - Exception grant flow (WF-4)
 * - Exception details display (WF-5)
 * - Evidence upload (WF-6)
 * - Mobile inspection simulator (WF-7)
 * - Audit trail (WF-8)
 * - Read-only underwriter view (WF-10)
 */

import { useState, forwardRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Finding, FindingSeverity, FindingStatus, Exception, PhotoType } from '../types/api';
import { cn } from '../utils/cn';
import { formatDate } from '../utils/formatDate';
import { Badge, severityToBadgeVariant } from './Badge';
import { Card } from './Card';
import { Button } from './Button';
import { ConfirmDialog } from './Modal';
import { Camera } from 'lucide-react';
// import { ExceptionModal } from './ExceptionModal'; // DEPRECATED: Direct exception granting removed per Worker 2
import { ExceptionDetailsBlock } from './ExceptionDetailsBlock';
import { EvidenceModal } from './EvidenceModal';
import { MobileInspectorSimulator, type InspectionPhoto } from './MobileInspectorSimulator';
import { AuditTrail } from './AuditTrail';
import { PhotoHeroCard, PhotoHeroGrid } from './PhotoHeroCard';
import { useEvidenceForFinding, useUploadEvidence, useCloseFinding } from '../hooks/useEvidence';
import { useToastActions } from './Toast';

// ============================================================================
// Types
// ============================================================================

export interface FindingCardProps {
  finding: Finding;
  onClick?: () => void;
  showPhotos?: boolean;
  className?: string;
  isReadOnly?: boolean;
  defaultExpanded?: boolean;
}

// ============================================================================
// Status Configuration (updated for E3/E4 lifecycle)
// ============================================================================

const statusConfig: Record<FindingStatus, {
  variant: 'danger' | 'warning' | 'success' | 'info' | 'default';
  label: string;
}> = {
  open: { variant: 'danger', label: 'Open' },
  exception_granted: { variant: 'info', label: 'Exception Granted' },
  evidence_submitted: { variant: 'warning', label: 'Evidence Submitted' },
  closed: { variant: 'success', label: 'Closed' },
};

const severityLabels: Record<FindingSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

// ============================================================================
// Component
// ============================================================================

export const FindingCard = forwardRef<HTMLDivElement, FindingCardProps>(
  ({ finding, onClick, showPhotos = false, className, isReadOnly = false, defaultExpanded = false }, ref) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    // const [showExceptionModal, setShowExceptionModal] = useState(false); // DEPRECATED: Direct exception granting removed
    const [showEvidenceModal, setShowEvidenceModal] = useState(false);
    const [showInspector, setShowInspector] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const navigate = useNavigate();
    const toast = useToastActions();
    const statusInfo = statusConfig[finding.status] || { variant: 'default' as const, label: finding.status };
    const severityVariant = severityToBadgeVariant(finding.severity);

    // Fetch exception data when expanded + exception_granted
    const { data: exceptionData } = useQuery({
      queryKey: ['findings', finding.id, 'exception'],
      queryFn: () => api.findings.getException(finding.id),
      enabled: isExpanded && finding.status === 'exception_granted',
    });
    const exception: Exception | null = exceptionData?.exception ?? null;

    // Fetch evidence when expanded and status is evidence_submitted or closed
    const { data: evidencePhotos } = useEvidenceForFinding(
      isExpanded && (finding.status === 'evidence_submitted' || finding.status === 'closed')
        ? finding.id
        : undefined
    );

    // Mutations
    const uploadEvidence = useUploadEvidence();
    const closeFinding = useCloseFinding();

    const handleToggleExpand = useCallback(() => {
      if (onClick) {
        onClick();
      } else {
        setIsExpanded((prev) => !prev);
      }
    }, [onClick]);

    // DEPRECATED: Direct exception granting removed per Worker 2
    // const handleExceptionSuccess = useCallback(() => {
    //   setShowExceptionModal(false);
    //   toast.success('Exception granted successfully');
    // }, [toast]);

    const handleEvidenceSubmit = useCallback(async (files: File[]) => {
      try {
        // 1. Upload each file to R2 via the uploads API
        const uploadResults = await Promise.all(
          files.map((file) =>
            api.uploads.uploadPhoto(
              crypto.randomUUID(),
              file,
              'other' as PhotoType,
              finding.id
            )
          )
        );

        // 2. Link uploaded photos to the finding and transition status
        uploadEvidence.mutate(
          {
            findingId: finding.id,
            photoIds: uploadResults.map((r) => r.id),
            source: 'direct_upload',
          },
          {
            onSuccess: () => toast.success('Evidence uploaded successfully'),
            onError: () => toast.error('Failed to submit evidence. Please try again.'),
          }
        );
      } catch {
        toast.error('Failed to upload photos. Submitting evidence without files.');
        uploadEvidence.mutate({
          findingId: finding.id,
          source: 'direct_upload',
        });
      }
    }, [finding.id, uploadEvidence, toast]);

    const handleInspectionSubmit = useCallback(async (photos: InspectionPhoto[]) => {
      // Mobile inspection photos are metadata-only (GPS coords, timestamps).
      // Submit as evidence with the photo metadata for audit trail.
      uploadEvidence.mutate(
        {
          findingId: finding.id,
          source: 'mobile_inspection',
        },
        {
          onSuccess: () => toast.success(`Inspection submitted (${photos.length} photo${photos.length !== 1 ? 's' : ''})`),
          onError: () => toast.error('Failed to submit inspection. Please try again.'),
        }
      );
    }, [finding.id, uploadEvidence, toast]);

    const handleCloseFinding = useCallback(() => {
      closeFinding.mutate(
        { findingId: finding.id },
        {
          onSuccess: () => toast.success('Finding closed successfully'),
          onError: () => toast.error('Failed to close finding. Please try again.'),
        }
      );
      setShowCloseConfirm(false);
    }, [finding.id, closeFinding, toast]);

    // Determine which action buttons to show
    // NOTE: Direct exception granting removed per Worker 2 - users must create subjectivity first
    // const canRequestException = finding.status === 'open' && !isReadOnly;
    const canUploadEvidence = finding.status === 'exception_granted' && !isReadOnly;
    const canCloseFinding = finding.status === 'evidence_submitted' && !isReadOnly;

    return (
      <>
        <Card
          ref={ref}
          hoverable
          onClick={handleToggleExpand}
          className={cn('relative bg-gray-50/80 dark:bg-slate-800/80 border border-gray-100/50 dark:border-slate-700/50', className)}
          data-testid={`finding-card-${finding.id}`}
        >
          {/* Collapsed View - Always visible */}
          <div className="flex items-start gap-3">
            {/* Checkbox indicator */}
            <div className="flex-shrink-0 mt-0.5">
              <div className={cn(
                'w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors',
                finding.status === 'closed'
                  ? 'bg-gray-900 border-gray-900 dark:bg-slate-300 dark:border-slate-300'
                  : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800'
              )}>
                {finding.status === 'closed' && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              {/* Title + Severity badge inline */}
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">
                  {finding.title}
                </h3>
                <Badge variant={severityVariant} size="sm">
                  {severityLabels[finding.severity].toUpperCase()}
                </Badge>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-500 dark:text-slate-400 leading-relaxed line-clamp-2">
                {finding.description}
              </p>

              {/* Photo Hero Cards (collapsed view) - square crops matching onboard camera */}
              {!isExpanded && showPhotos && finding.photoUrls && finding.photoUrls.length > 0 && (
                <div className="mt-3">
                  {finding.photoUrls && finding.photoUrls.length > 0 ? (
                    <>
                      <PhotoHeroGrid
                        photos={[]}
                        photoUrls={finding.photoUrls}
                        maxVisible={4}
                        size="sm"
                      />
                      <span className="mt-1.5 inline-flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {finding.photoUrls.length} photo{finding.photoUrls.length !== 1 ? 's' : ''}
                      </span>
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gray-400 dark:bg-slate-500 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-500 dark:bg-slate-400"></span>
                      </span>
                      Upload needed
                    </span>
                  )}
                </div>
              )}

              {/* Action links row (collapsed view) */}
              {!isExpanded && (
                <div className="mt-3 pt-3 border-t border-gray-200/60 dark:border-slate-700/60 flex items-center gap-4 text-sm">
                  {finding.status === 'open' && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowEvidenceModal(true); }}
                        className="text-gray-700 dark:text-slate-300 font-semibold hover:text-gray-900 dark:hover:text-slate-100 flex items-center gap-1.5"
                      >
                        <Camera size={12} />
                        Upload Proof
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/subjectivities/create?findingId=${finding.id}&policyId=${finding.policyId}`); }}
                        className="text-gray-400 dark:text-slate-500 font-medium hover:text-gray-600 dark:hover:text-slate-300"
                      >
                        Mark Waived
                      </button>
                    </>
                  )}
                  {finding.status !== 'open' && (
                    <span className="text-xs text-gray-400 dark:text-slate-500">
                      Detected: {formatDate(finding.detectedAt)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Expand/Collapse chevron */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded((prev) => !prev);
              }}
              className="ml-1 p-1 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 flex-shrink-0"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={cn('w-5 h-5 transition-transform duration-200', isExpanded && 'rotate-180')}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* ============================================================ */}
          {/* EXPANDED VIEW */}
          {/* ============================================================ */}
          {isExpanded && (
            <div className="mt-4" onClick={(e) => e.stopPropagation()}>
              {/* Metadata */}
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-400 mb-4">
                <span>Detected: {formatDate(finding.detectedAt)}</span>
                {finding.resolvedAt && (
                  <span>Resolved: {formatDate(finding.resolvedAt)}</span>
                )}
                <span className="text-gray-300 dark:text-slate-600">|</span>
                <span>Type: {finding.type.replace(/_/g, ' ')}</span>
              </div>

              {/* Exception Details Block (when exception_granted) */}
              {finding.status === 'exception_granted' && exception && (
                <ExceptionDetailsBlock exception={exception} photoUrls={finding.photoUrls} />
              )}

              {/* Evidence Photos - square hero cards with gallery links */}
              {(finding.status === 'evidence_submitted' || finding.status === 'closed') && evidencePhotos && evidencePhotos.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-800">
                  <PhotoHeroGrid
                    photos={evidencePhotos}
                    maxVisible={4}
                    size="md"
                    label="Evidence Photos"
                  />
                </div>
              )}

              {/* Action Buttons */}
              {!isReadOnly && (
                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-800 flex flex-wrap gap-2">
                  {/* REMOVED: Direct exception granting per Worker 2 - users must create subjectivity first, then waive it
                  {canRequestException && (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => setShowExceptionModal(true)}
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Request Exception
                    </Button>
                  )}
                  */}
                  {finding.status === 'open' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => navigate(`/subjectivities/create?findingId=${finding.id}&policyId=${finding.policyId}`)}
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      Escalate to Subjectivity
                    </Button>
                  )}
                  {canUploadEvidence && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setShowEvidenceModal(true)}
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Upload Evidence
                    </Button>
                  )}
                  {canCloseFinding && (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => setShowCloseConfirm(true)}
                      className="bg-gray-900 hover:bg-gray-800 dark:bg-slate-300 dark:hover:bg-slate-400 dark:text-slate-900"
                    >
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Close Finding
                    </Button>
                  )}
                </div>
              )}

              {/* Read-only indicator */}
              {isReadOnly && (
                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-800">
                  <p className="text-xs text-gray-400 dark:text-slate-500 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Read-only view
                  </p>
                </div>
              )}

              {/* Audit Trail */}
              <AuditTrail resourceType="finding" resourceId={finding.id} />
            </div>
          )}
        </Card>

        {/* Modals (rendered outside card to avoid z-index issues) */}
        {!isReadOnly && (
          <>
            {/* REMOVED: Direct exception granting per Worker 2
            <ExceptionModal
              isOpen={showExceptionModal}
              onClose={() => setShowExceptionModal(false)}
              finding={finding}
              onSuccess={handleExceptionSuccess}
            />
            */}
            <EvidenceModal
              isOpen={showEvidenceModal}
              onClose={() => setShowEvidenceModal(false)}
              finding={finding}
              onRequestInspection={() => {
                setShowEvidenceModal(false);
                setShowInspector(true);
              }}
              onSubmit={handleEvidenceSubmit}
            />
            <MobileInspectorSimulator
              isOpen={showInspector}
              onClose={() => setShowInspector(false)}
              finding={finding}
              onSubmit={handleInspectionSubmit}
            />
            <ConfirmDialog
              isOpen={showCloseConfirm}
              onClose={() => setShowCloseConfirm(false)}
              onConfirm={handleCloseFinding}
              title="Close Finding"
              message="This will mark the finding as closed and verified. Evidence has been reviewed and the remediation is complete."
              confirmLabel="Close Finding"
              variant="primary"
              loading={closeFinding.isPending}
            />
          </>
        )}
      </>
    );
  }
);

FindingCard.displayName = 'FindingCard';

export default FindingCard;
